/**
 * Buyer tax identity, mirrored from the server's
 * models/shared/buyerIdentityFields.js for immediate feedback in the browser
 * ONLY. The server remains the single judge: it re-checks every id, and a
 * malformed one is stored as an anomaly, never a rejected order. A walk-in
 * captures nothing, so no buyer block is sent, which is exactly how the server
 * reads an absent buyer. This is an optional buyer identifier, not a fiscal
 * receipt: capturing it here changes nothing about how the order is priced.
 *
 * Kept as one module so the checkout section, the payload builder and the
 * confirmation summary share exactly one copy of the rules rather than drifting.
 */

// The codes the server reads. The UI shows the labels below; the codes match the
// POS (BuyerIdentityRules) so both clients speak the same shape to addOrderItems.
export const BUYER_TYPE_CONSUMER = "B2C";
export const BUYER_TYPE_BUSINESS = "B2B";
export const BUYER_TYPE_FOREIGNER = "FOREIGNER";

export const BUYER_ID_TIN = "TIN";
export const BUYER_ID_BRN = "BRN";
export const BUYER_ID_NIN = "NIN";

// What the shopper sees, paired with the code that goes on the payload.
export const BUYER_TYPE_OPTIONS = [
  { value: BUYER_TYPE_CONSUMER, label: "Consumer" },
  { value: BUYER_TYPE_BUSINESS, label: "Business" },
  { value: BUYER_TYPE_FOREIGNER, label: "Foreigner" },
];

export const BUYER_ID_TYPE_OPTIONS = [
  { value: BUYER_ID_TIN, label: "TIN" },
  { value: BUYER_ID_BRN, label: "BRN" },
  { value: BUYER_ID_NIN, label: "NIN" },
];

/**
 * Strip an identifier to the characters that carry meaning: whitespace removed,
 * uppercased. People type TINs with spaces off a certificate, and a lowercase
 * transcription of a NIN or BRN is the same identifier.
 */
export function canonicaliseBuyerId(value) {
  return String(value == null ? "" : value).replace(/\s/g, "").toUpperCase();
}

/** The human hint for an id type, matching the server's wording. */
export function buyerIdHint(idType) {
  switch (String(idType == null ? "" : idType).trim().toUpperCase()) {
    case BUYER_ID_TIN:
      return "9 to 14 digits";
    case BUYER_ID_BRN:
      return "4 to 30 letters, digits, dot, slash or dash";
    case BUYER_ID_NIN:
      return "14 letters or digits";
    default:
      return "";
  }
}

// Bounds match the server's ID_SHAPES exactly. Wide on purpose where URA's
// published format is not something we hold in writing: rejecting a real
// number because our pattern was tighter than the authority's is the expensive
// failure, and a malformed one costs only a submission the server flags.
const BUYER_ID_SHAPES = {
  [BUYER_ID_TIN]: /^[0-9]{9,14}$/,
  [BUYER_ID_BRN]: /^[A-Z0-9][A-Z0-9./-]{3,29}$/,
  [BUYER_ID_NIN]: /^[A-Z0-9]{14}$/,
};

/**
 * Is this identifier the right SHAPE for its kind? Never a claim that the number
 * exists or belongs to whoever presented it: shape only, exactly as the server's
 * isWellFormedBuyerId. A hint for the shopper, never a gate on the order.
 */
export function isWellFormedBuyerId(idType, idNumber) {
  const shape = BUYER_ID_SHAPES[String(idType == null ? "" : idType).trim().toUpperCase()];
  return shape ? shape.test(canonicaliseBuyerId(idNumber)) : false;
}

/**
 * True when the shopper captured anything worth sending. Nothing captured is a
 * walk-in: no buyer block is sent, which is how the server reads an absent buyer.
 */
export function buyerHasContent(fields) {
  const f = fields || {};
  const hasNumber = String(f.idNumber == null ? "" : f.idNumber).trim() !== "";
  const hasName = String(f.name == null ? "" : f.name).trim() !== "";
  return hasNumber || hasName || (f.type && f.type !== BUYER_TYPE_CONSUMER);
}

/**
 * Build the nested `buyer` block for the order payload, or null when the shopper
 * captured nothing. The id number is canonicalised and the name trimmed. A
 * malformed id is still sent: the server is the judge, and a bad number becomes
 * an anomaly it flags, never an order it refuses.
 */
export function buildBuyerPayload(fields) {
  if (!buyerHasContent(fields)) return null;
  const f = fields || {};
  const cleanNumber = canonicaliseBuyerId(f.idNumber);
  const cleanName = String(f.name == null ? "" : f.name).trim();
  return {
    type: f.type || BUYER_TYPE_CONSUMER,
    idType: cleanNumber ? f.idType || null : null,
    idNumber: cleanNumber || null,
    name: cleanName || null,
  };
}

/**
 * A one-line summary of a captured buyer for the order confirmation, or null
 * when there is nothing to show.
 */
export function formatBuyerSummary(buyer) {
  if (!buyer) return null;
  const idType = buyer.idType ? String(buyer.idType).trim() : "";
  const idNumber = buyer.idNumber ? String(buyer.idNumber).trim() : "";
  const name = buyer.name ? String(buyer.name).trim() : "";
  if (idType && idNumber) {
    return name ? `Buyer ${idType} ${idNumber} (${name})` : `Buyer ${idType} ${idNumber}`;
  }
  if (idNumber) {
    return name ? `Buyer ${idNumber} (${name})` : `Buyer ${idNumber}`;
  }
  if (name) {
    return `Buyer ${name}`;
  }
  return null;
}
